import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { ReportsService } from 'app/modules/admin/transactions/inwards/reports.service';
import { AlertMessageService } from 'app/core/common/alert.message.service';
import { BaseRespone } from 'app/core/models/common.response';
import { Observable, of, Subject } from 'rxjs';
import { map, startWith, take, takeUntil } from 'rxjs/operators';
import { InwardReport } from 'app/modules/admin/transactions/inwards/reports.types';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { LookupService } from 'app/core/common/services/lookup.service';
import { CommonDropDown } from 'app/core/common/types/dropdown.types';
import { ApprovedMeasurementDetailsComponent } from '../details/approved-measurement-details.component';
import { ApprovedMeasurementsService } from '../approved-measurement.service';
import { ApprovedMeasurement } from '../approved-measurement.types';

@Component({
    selector: 'app-land-area-approved-measurement',
    templateUrl: './land-area-approved-measurement.component.html',
})
export class LandAreaApprovedMeasurementComponent implements OnInit, OnDestroy {
    @Output('calculationAreas') calculationAreas: EventEmitter<any> =
        new EventEmitter();
    @Input() report: InwardReport;
    @Input() reportId: number;
    @Input() reportType: string;
    selection = new SelectionModel<ApprovedMeasurement>(true, []);
    approvedMeasurementForm: FormGroup;
    totalArea: number = 0.0;
    totalBuiltUp: number = 0.0;
    totalSuperBuiltUp: number = 0.0;
    selectedIds: number[] = [];
    selectedApprovedMeasurement: ApprovedMeasurement;
    isAllSelectedCheckBox: boolean = false;
    options: CommonDropDown[] = [];
    filteredOptions: { [index: number]: CommonDropDown[] } = {};
    approvedMeasurementform: FormGroup;
    landAreaType: string = 'Land Area'
    landUpApprovedMeasurements$: Observable<ApprovedMeasurement[]>;
    alert: any;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _matDialog: MatDialog,
        private _fuseConfirmationService: FuseConfirmationService,
        private _approvedMeasurementService: ApprovedMeasurementsService,
        private _alertMessageService: AlertMessageService,
        private _lookupService: LookupService,
        private _formBuilder: FormBuilder,
        private approvedMeasurementService: ApprovedMeasurementsService
    ) { }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    ngOnInit(): void {
        this._approvedMeasurementService
            .getLandUpApprovedMeasurements(this.reportId)
            .subscribe((res) => {
                this.calculateApprovedMeasurementsTotal();
            });

        this.landUpApprovedMeasurements$ =
            this._approvedMeasurementService.landUpApprovedMeasurements$.pipe(
                map((data) => {
                    data?.sort((a, b) => a.orderIndex - b.orderIndex);

                    // Initialize filteredOptions for each row
                    if (data) {
                        data.forEach((m, i) => {
                            this.filterParticularOptions(m, i);
                        });
                    }
                    return data;
                })
            );

        this._approvedMeasurementService.approvedMeasurement$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((contact: ApprovedMeasurement) => {
                this.selectedApprovedMeasurement = contact;
            });

        this.approvedMeasurementform = this._formBuilder.group({
            id: [''],
            reportId: [this.reportId],
            type: [this.landAreaType, [Validators.required]],
            particular: [''],
            width: [0],
            depth: [0],
            total: [0],
        });

        this._lookupService
            .getOptionByTypeId(92, this.reportType)
            .subscribe((res) => {
                this.options = res;
                this.landUpApprovedMeasurements$.pipe(take(1)).subscribe(approvedMeasurements => {
                    if (approvedMeasurements) {
                        approvedMeasurements.forEach((m, i) => {
                            this.filterParticularOptions(m, i);
                        });
                    }
                });
            });
    }

    displayOptionsFn = (value: any): string => {
        if (!value) return '';
        // If value is a string, find the option object
        if (typeof value === 'string') {
            const match = this.options.find(opt => opt.name === value);
            return match ? match.name : value;
        }
        // If value is an object
        return value.name || '';
    };

    new(selectedType?: string): void {
        this._approvedMeasurementService.addNewApprovedMeasurement(this.reportId, selectedType);
    }

    calculateTotal(approved: ApprovedMeasurement): void {
        if (approved.width && approved.depth) {
            approved.total = Number(
                (approved.width * approved.depth).toFixed(2)
            );
        } else {
            approved.total = 0;
        }
    }

    cancelNewRow(index: number) {
        this._approvedMeasurementService.cancelLandRow(index);
    }

    save(approvedMeasurement: ApprovedMeasurement) {
        if (
            !approvedMeasurement.particular ||
            approvedMeasurement.particular.trim() === ''
        ) {
            this.alert = {
                type: 'error',
                message: 'Please select particular'
            };
            return;
        }
        if (approvedMeasurement.id !== null && approvedMeasurement.id !== 0) {
            this.approvedMeasurementService
                .updateApprovedMeasurement(approvedMeasurement, this.landAreaType)
                .subscribe((response) => {
                    if (response.isSuccess) {
                        // Show a success message
                        this.alert = {
                            type: 'success',
                            message: 'Approved Measurement updated successfully!'
                        };
                        this.calculateApprovedMeasurementsTotal();
                        approvedMeasurement.isEditing = false;
                    } else {
                        this._alertMessageService.error(
                            response.errors.join('\n')
                        );
                    }
                });
        } else {
            this.approvedMeasurementService
                .createApprovedMeasurement(approvedMeasurement, this.landAreaType)
                .subscribe((response) => {
                    if (response.isSuccess) {
                        // Show a success message
                        this.alert = {
                            type: 'success',
                            message: 'Approved measurement added successfully'
                        };
                        this.calculateApprovedMeasurementsTotal();
                        approvedMeasurement.isEditing = false;
                    } else {
                        this._alertMessageService.error(
                            response.errors.join('\n')
                        );
                    }
                });
        }
    }

    edit(approvedMeasurement: ApprovedMeasurement): void {
        this._matDialog
            .open(ApprovedMeasurementDetailsComponent, {
                autoFocus: false,
                data: {
                    approvedMeasurement: approvedMeasurement,
                    reportType: this.reportType,
                },
            })
            .afterClosed()
            .subscribe((response: BaseRespone<ApprovedMeasurement>) => { });
    }

    /**
     * Delete the selected product using the form data
     */
    delete(id: number): void {
        // Open the confirmation dialog
        const confirmation = this._fuseConfirmationService.open({
            title: 'Delete Approved Measurement',
            message:
                'Are you sure you want to remove? This action cannot be undone!',
            actions: {
                confirm: {
                    label: 'Delete',
                },
            },
        });

        // Subscribe to the confirmation dialog closed action
        confirmation.afterClosed().subscribe((result) => {
            // If the confirm button pressed...
            if (result === 'confirmed') {
                //Delete the product on the server
                this._approvedMeasurementService
                    .deleteApprovedMeasurement(id, this.landAreaType)
                    .subscribe((res) => {
                        if (res.isSuccess) {
                            this.alert = {
                                type: 'success',
                                message: 'Approved measurement deleted successfully'
                            };
                            this.calculateApprovedMeasurementsTotal();
                            this.landUpApprovedMeasurements$.pipe(take(1)).subscribe(data => {
                                if (data) {
                                    this.updateOrderIndex(data);
                                }
                            });
                        } else {
                            this._alertMessageService.error(
                                res.errors.join('\n')
                            );
                        }
                    });
            }
        });
    }

    async deleteSelectedRows() {
        const confirmation = this._fuseConfirmationService.open({
            title: 'Delete All Approved Measurement',
            message:
                'Are you sure you want to remove? This action cannot be undone!',
            actions: {
                confirm: {
                    label: 'Delete',
                },
            },
        });

        const result = await confirmation.afterClosed().toPromise();

        if (result === 'confirmed') {
            const ids = this.selection.selected.map((item) => item.id);
            const res = await this._approvedMeasurementService
                .deleteAllApprovedMeasurement(ids, this.landAreaType)
                .toPromise();

            if (res.isSuccess) {
                this.alert = {
                    type: 'success',
                    message: 'Approved measurement all deleted successfully'
                };
                this.calculateApprovedMeasurementsTotal();
                this.landUpApprovedMeasurements$.pipe(take(1)).subscribe(data => {
                    if (data) {
                        this.updateOrderIndex(data);
                    }
                });
                this.selection.clear();
                this.landUpApprovedMeasurements$.subscribe();
            } else {
                this.alert = {
                    type: 'error',
                    message: res.errors.join('\n')
                };
            }
        }
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    calculateApprovedMeasurementsTotal(): void {
        this._approvedMeasurementService.landUpApprovedMeasurements$.pipe(take(1)).subscribe((res) => {
            this.report.approvedArea = Number(
                res
                    // .filter((a) => a.type === 'Land Area')
                    .filter((a) => a.type === this.landAreaType)
                    .reduce((sum, current) => sum + current.total, 0)
                    .toFixed(2)
            );

            this.calculationAreas.emit();
        });
    }

    updateCheckedList(event, row) {
        this.landUpApprovedMeasurements$.pipe(take(1)).subscribe((measurements) => {
            measurements.forEach((item) => {
                if (item.id == row.id) {
                    item.selected = event.checked;
                    if (item.selected) {
                        this.selection.select(item); // Select item in selection model
                    } else {
                        this.selection.deselect(item);
                    }
                }
            });

            this.isAllSelectedCheckBox = !measurements.some((a) => !a.selected);
        });
    }

    selectAll(event: any) {
        const checked = event.checked;
        this.landUpApprovedMeasurements$
            .pipe(take(1))
            .subscribe((measurements: any[]) => {
                measurements.forEach((measurement) => {
                    measurement.selected = checked;
                });
            });
    }
    checkboxLabel() {
        if (this.isAllSelected()) {
            return 'Deselect all';
        }
        if (this.selection.isEmpty()) {
            return 'Select all';
        }
        return `${this.selection.selected.length} selected`;
    }

    isAllSelected() {
        // Check if all measurements are selected
        return this.landUpApprovedMeasurements$.pipe(
            take(1),
            map((measurements) =>
                measurements.every((measurement) => measurement.selected)
            )
        );
    }

    toggleAllRows() {
        this.isAllSelected().subscribe((allSelected) => {
            if (!allSelected) {
                // Select all items
                this.landUpApprovedMeasurements$
                    .pipe(take(1))
                    .subscribe((measurements) => {
                        measurements.forEach((item) => {
                            item.selected = true;
                            this.selection.select(item); // Select item in selection model
                        });
                    });
                this.isAllSelectedCheckBox = true;
            } else {
                // Deselect all items
                this.landUpApprovedMeasurements$
                    .pipe(take(1))
                    .subscribe((measurements) => {
                        measurements.forEach((item) => {
                            item.selected = false;
                            this.selection.deselect(item); // Deselect item in selection model
                        });
                    });
                this.isAllSelectedCheckBox = false;
            }
        });
    }

    drop(event: CdkDragDrop<ApprovedMeasurement[]>): void {
        // Retrieve current data from the observable
        this.landUpApprovedMeasurements$.pipe(take(1)).subscribe((data) => {
            if (data) {
                // Reorder locally
                moveItemInArray(data, event.previousIndex, event.currentIndex);

                this.updateOrderIndex(data);
            }
        });
    }

    updateOrderIndex(data: ApprovedMeasurement[]): void {
        data.forEach((item, index) => {
            item.orderIndex = index + 1;
            this._approvedMeasurementService.updateApprovedMeasurement(item, this.landAreaType).subscribe();
        });
    }

    oldPopup(selectedValue?: string) {
        let orderIndex = 0;
        this.landUpApprovedMeasurements$.subscribe((result) => {
            orderIndex = result.length + 1;
        });

        this._matDialog
            .open(ApprovedMeasurementDetailsComponent, {
                autoFocus: false,
                data: {
                    approvedMeasurement: {
                        orderIndex: orderIndex,
                        type: '',
                        particular: '',
                        width: '',
                        depth: '',
                        total: '',
                        reportId: this.reportId,
                    },
                    reportType: this.reportType,
                    selectedType: selectedValue,
                },
            })
            .afterClosed()
            .subscribe((response) => { });
    }

    originalValues = new Map<string, any>();

    getKey(approvedMeasurement: any, field: string): string {
        return (approvedMeasurement.id ?? approvedMeasurement.tempId ?? JSON.stringify(approvedMeasurement)) + '_' + field;
    }

    // Called when field is focused to store the original value
    onInputInit(approvedMeasurement: any, field: string) {
        const key = this.getKey(approvedMeasurement, field);
        if (!this.originalValues.has(key)) {
            this.originalValues.set(key, this.cloneValue(approvedMeasurement[field]));
        }
    }

    // Called on each value change
    onFieldChange(approvedMeasurement: any, field: string) {
        const key = this.getKey(approvedMeasurement, field);
        const original = this.originalValues.get(key);
        const current = approvedMeasurement[field];

        if (!this.deepEqual(original, current)) {
            this.save(approvedMeasurement); // auto-save logic
            this.originalValues.set(key, this.cloneValue(current)); // update cache
        }
    }

    // Utility: deep compare for objects/arrays/primitives
    deepEqual(a: any, b: any): boolean {
        return JSON.stringify(a) === JSON.stringify(b);
    }

    // Utility: clone value (for caching original values safely)
    cloneValue(val: any): any {
        return JSON.parse(JSON.stringify(val));
    }

     filterParticularOptions(approvedMeasurement: any, index: number) {
        if (!this.options) {
            this.filteredOptions[index] = [];
            return;
        }
        const value = approvedMeasurement.particular || '';
        const filterValue = value.toLowerCase();
        this.filteredOptions[index] = this.options.filter(option =>
            option.name.toLowerCase().includes(filterValue)
        );
    }


    // Called when user selects from autocomplete
    onAutocompleteSelected(approvedMeasurement: any, value: string) {
        approvedMeasurement.particular = value;
        this.onFieldChange(approvedMeasurement, 'particular');
    }
}

